import { buildTaxonomyHref } from 'src/adapters/storeToPageURL/facets/buildTaxonomyHref';
import { findActiveOption } from 'src/redux/productsQuery/helpers/facetOptionHelpers';
import type { TaxonomyContext } from 'src/redux/productsQuery/selectors/selectTaxonomyContext';

export interface TaxonomyParentLink {
  /** The name of the level the link points at, used for the aria-label. */
  destinationName: string;
  href: string;
  /** The visible link text. */
  label: string;
}

/**
 * The "back to parent level" link shown in front of the taxonomy nav, pointing
 * one or two levels up. It reads "Shop more" (or "Shop all" at the shop-surface
 * root); the destination category's name is carried separately for the
 * aria-label.
 *
 * When the current page is the deepest level of its branch (no children to drill
 * into), the nav is in "siblings" mode and its own "All" entry already links one
 * level up — so the back link skips that and goes two levels up. Otherwise (the
 * nav shows children), it goes one level up. The hop is clamped to one when two
 * would pass the top-level department. Returns null when there is no ancestor
 * category to point at (depth < 2).
 */
export const adaptTaxonomyContextToParentLink = (
  context: TaxonomyContext,
): TaxonomyParentLink | null => {
  const { depth, facets, isDeepestLevel, surface, taxonomyAttributeAliases, unsupported } = context;

  if (unsupported || depth < 1) {
    return null;
  }

  const levelsBack = isDeepestLevel ? 2 : 1;
  const targetDepth = Math.max(depth - levelsBack, 0);

  if (targetDepth === 0) {
    return {
      href: buildTaxonomyHref({
        facets,
        levelCount: 0,
        surface,
        taxonomyAttributeAliases,
      }),
      label: 'Shop all',
      destinationName: 'Shop all',
    };
  }

  const targetOption = findActiveOption(facets, taxonomyAttributeAliases[targetDepth - 1]!);
  if (!targetOption) {
    return null;
  }

  return {
    href: buildTaxonomyHref({
      facets,
      levelCount: targetDepth,
      surface,
      taxonomyAttributeAliases,
    }),
    label: 'Shop more',
    // Raw option title; character-casing is applied by the rendering component.
    destinationName: targetOption.title,
  };
};
